import { useCallback, useMemo, useState } from "react";
import { Pressable } from "react-native";
import { LegendList } from "@legendapp/list";
import { Card, Text, View } from "@src/components";
import type { WasteSenseEventRow, WasteSenseSite } from "../types";
import type { ImageProvider } from "../imageProvider";
import { isPotentialIncident, isPriorityIncident } from "../priority";
import { EventSnapshotLightbox } from "./EventSnapshotLightbox";
import { LazySnapshot } from "./LazySnapshot";
import { activityTone, formatEventTimestamp } from "./eventDisplay";

interface EventsTableProps {
  rows: readonly WasteSenseEventRow[];
  imageProvider: ImageProvider;
  /**
   * The site that owns these rows. Drives timestamp formatting (the
   * site's IANA `timezone` is the local zone for every row, so the
   * weekday display agrees with `dailyActivity.ts`) and is forwarded
   * to the image provider so the placeholder swatch can render the
   * site's address alongside the camera and timestamp.
   */
  site: WasteSenseSite;
  /** Vertical viewport for the rows. Rows scroll inside this height. */
  maxHeight?: number;
}

/**
 * Column layout. Most columns are fluid: `flex-grow` apportions
 * slack width across the row above the column's `flex-basis`. The
 * snapshot column is fixed-width because its image is fixed-size.
 *
 * Header and rows share this single source so they can't drift.
 */
type ColumnKey = "time" | "camera" | "activity" | "description" | "thumb";

interface Column {
  key: ColumnKey;
  label: string;
  /** Proportional `flex-grow` weight when fluid; `0` for fixed columns. */
  flex: number;
  /** `flex-basis` for fluid columns; explicit width for fixed columns. */
  baseWidth: number;
}

const COLUMNS: readonly Column[] = [
  { key: "time", label: "Time", flex: 1.4, baseWidth: 140 },
  { key: "camera", label: "Camera", flex: 1.4, baseWidth: 140 },
  { key: "activity", label: "Activity", flex: 1.2, baseWidth: 120 },
  { key: "description", label: "Description", flex: 3, baseWidth: 240 },
  { key: "thumb", label: "Snapshot", flex: 0, baseWidth: 110 },
];

/**
 * Average row height for the virtualizer. Single-line descriptions
 * land near 80px; rows with `interesting_tags` push to ~96px. Picking
 * the high end means the scrollbar settles a tad short rather than
 * overshooting, which is the less jarring of the two failure modes.
 */
const ESTIMATED_ROW_HEIGHT = 96;

const DEFAULT_MAX_HEIGHT = 640;

/**
 * Visible event subset. Mirrors the predicates used by the priority
 * hero and KPIs, so toggling here filters down to the same rows the
 * other surfaces count — never a different definition of "priority"
 * or "potential". `all` is the unfiltered view (default).
 */
type EventFilter = "all" | "priority" | "potential";

interface FilterOption {
  value: EventFilter;
  label: string;
}

const FILTER_OPTIONS: readonly FilterOption[] = [
  { value: "priority", label: "Priority only" },
  { value: "potential", label: "Potential only" },
  { value: "all", label: "All" },
];

/**
 * Detected events log table. Mirrors the static report's
 * `.log-table` — Time, Camera, Activity, Description, Thumb.
 *
 * Rows are virtualized via `LegendList`: the table viewport is
 * capped at `maxHeight` and rows scroll vertically inside it,
 * with offscreen rows recycled. Columns are fluid (flex-weighted
 * with `flex-basis` floors) so the table fills its parent's
 * width and reapportions slack proportionally.
 *
 * A filter toggle sits above the table — All / Priority only /
 * Potential only — using the same predicates the KPI strip and
 * priority hero rely on, so a filter pill never disagrees with
 * the headline counts.
 */
export function EventsTable({ rows, imageProvider, site, maxHeight = DEFAULT_MAX_HEIGHT }: EventsTableProps) {
  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => (a.timestamp < b.timestamp ? 1 : -1));
  }, [rows]);

  const [filter, setFilter] = useState<EventFilter>("priority");

  const visibleRows = useMemo(() => {
    if (filter === "priority") return sortedRows.filter(isPriorityIncident);
    if (filter === "potential") return sortedRows.filter(isPotentialIncident);
    return sortedRows;
  }, [sortedRows, filter]);

  /**
   * Active snapshot lives on the table so virtualizer row recycling
   * can't strand the open lightbox on a row that scrolled offscreen.
   */
  const [activeEvent, setActiveEvent] = useState<WasteSenseEventRow | null>(null);

  const handleOpenSnapshot = useCallback((row: WasteSenseEventRow) => {
    setActiveEvent(row);
  }, []);

  const handleCloseSnapshot = useCallback(() => {
    setActiveEvent(null);
  }, []);

  const renderItem = useCallback(
    ({ item }: { item: WasteSenseEventRow }) => (
      <EventRow row={item} imageProvider={imageProvider} site={site} onOpenSnapshot={handleOpenSnapshot} />
    ),
    [imageProvider, site, handleOpenSnapshot],
  );

  const keyExtractor = useCallback((item: WasteSenseEventRow) => item.event_id, []);

  const activeImageUrl = activeEvent ? imageProvider.getEventImageUrl(activeEvent, { site }) : null;

  const headerLabel = formatHeaderLabel(filter, visibleRows.length);
  const emptyMessage =
    sortedRows.length === 0 ? "No events recorded for this site in the reporting window."
    : filter === "priority" ? "No priority incidents in the reporting window."
    : filter === "potential" ? "No potential incidents in the reporting window."
    : "No events recorded for this site in the reporting window.";

  return (
    <View gap="sm">
      <View flexDirection="row" justifyContent="space-between" alignItems="center" gap="sm" flexWrap="wrap">
        <Text textStyle="xs/semibold" color="textMuted" letterSpacing={0.5} textTransform="uppercase">
          {headerLabel}
        </Text>
        <FilterToggle value={filter} onChange={setFilter} />
      </View>

      {visibleRows.length === 0 ?
        <Card>
          <Text textStyle="sm/regular" color="textMuted">
            {emptyMessage}
          </Text>
        </Card>
      : <View>
          <HeaderRow />
          {/*
           * `key={filter}` forces the virtualizer to remount when the
           * dataset shape changes. `LegendList` retains internal layout
           * state (item positions, recycled cell offsets) keyed on
           * indices, not item identity — so swapping a 5-row dataset
           * for a 60-row one can leave the rendered cells anchored to
           * the old positions until a scroll event re-triggers layout.
           * The user-visible symptom is a blank viewport that "snaps"
           * into place on the first scroll. A fresh mount with the
           * new data is the cheapest cure: scroll position resets to
           * 0 (which is what the user expects when they pick a new
           * filter anyway) and layout is computed against the new
           * dataset from a clean slate.
           */}
          <LegendList
            key={filter}
            data={visibleRows}
            renderItem={renderItem}
            keyExtractor={keyExtractor}
            estimatedItemSize={ESTIMATED_ROW_HEIGHT}
            recycleItems
            drawDistance={ESTIMATED_ROW_HEIGHT * 4}
            showsVerticalScrollIndicator
            style={{ maxHeight }}
          />
        </View>
      }

      <EventSnapshotLightbox
        event={activeEvent}
        imageUrl={activeImageUrl}
        timezone={site.timezone}
        onClose={handleCloseSnapshot}
      />
    </View>
  );
}

/**
 * Composes the column header line so the count always reflects the
 * filtered subset — keeping the headline ("12 priority events")
 * truthful when a filter narrows the table.
 */
function formatHeaderLabel(filter: EventFilter, count: number): string {
  const formatted = count.toLocaleString();
  if (filter === "priority") {
    const noun = count === 1 ? "priority event" : "priority events";
    return `Detected events log (7-day period) — ${formatted} ${noun}`;
  }
  if (filter === "potential") {
    const noun = count === 1 ? "potential event" : "potential events";
    return `Detected events log (7-day period) — ${formatted} ${noun}`;
  }
  const noun = count === 1 ? "event" : "events";
  return `Detected events log (7-day period) — ${formatted} ${noun}`;
}

interface FilterToggleProps {
  value: EventFilter;
  onChange: (next: EventFilter) => void;
}

/**
 * Segmented filter control. Each option is a Pressable styled as a
 * pill; the active option flips to the surface-on-muted treatment
 * matching the other inline toggles in the report. Uses
 * `accessibilityState.selected` so screen readers announce the
 * current filter without a separate live region.
 */
function FilterToggle({ value, onChange }: FilterToggleProps) {
  return (
    <View
      flexDirection="row"
      bg="muted"
      rounded={999}
      p="xxs"
      gap="xxs"
      accessibilityRole="tablist"
      accessibilityLabel="Filter detected events"
    >
      {FILTER_OPTIONS.map((option) => {
        const isActive = option.value === value;
        return (
          <Pressable
            key={option.value}
            onPress={() => onChange(option.value)}
            accessibilityRole="tab"
            accessibilityState={{ selected: isActive }}
            accessibilityLabel={option.label}
            style={({ pressed }) => ({ opacity: pressed ? 0.85 : 1 })}
          >
            {/*
             * Active pill gets the surface treatment so it sits on
             * top of the parent's `muted` track. Inactive pills
             * inherit the parent's track colour by omitting `bg` —
             * the View `bg` prop is typed against the theme palette
             * and has no `transparent` token, and we want the track
             * to show through anyway.
             */}
            {isActive ?
              <View bg="surface" px="sm" py="xxs" rounded={999}>
                <Text textStyle="xs/semibold" color="text">
                  {option.label}
                </Text>
              </View>
            : <View px="sm" py="xxs" rounded={999}>
                <Text textStyle="xs/semibold" color="textMuted">
                  {option.label}
                </Text>
              </View>
            }
          </Pressable>
        );
      })}
    </View>
  );
}

function HeaderRow() {
  return (
    <View flexDirection="row" bg="muted" py="xs" px="sm" borderBottomWidth={1} borderColor="borderCard">
      {COLUMNS.map((column) => (
        <Cell key={column.key} column={column}>
          <Text textStyle="xs/semibold" color="textMuted" letterSpacing={0.5} textTransform="uppercase">
            {column.label}
          </Text>
        </Cell>
      ))}
    </View>
  );
}

interface CellProps {
  column: Column;
  children: React.ReactNode;
}

/**
 * Column cell for both header and body rows. Fluid columns carry
 * a `flex-basis` (the column's "natural" width) and a `flex-grow`
 * weight (how slack is shared above it). Fixed columns (`flex: 0`)
 * get an explicit width. `style={{ minWidth: 0 }}` on flex cells
 * defeats the flexbox `min-width: auto` default on web so a long
 * unbroken token in the description doesn't blow out the column.
 */
function Cell({ column, children }: CellProps) {
  if (column.flex === 0) {
    return (
      <View width={column.baseWidth} px="xs">
        {children}
      </View>
    );
  }
  return (
    <View flexGrow={column.flex} flexShrink={1} flexBasis={column.baseWidth} px="xs" style={{ minWidth: 0 }}>
      {children}
    </View>
  );
}

interface EventRowProps {
  row: WasteSenseEventRow;
  imageProvider: ImageProvider;
  site: WasteSenseSite;
  onOpenSnapshot: (row: WasteSenseEventRow) => void;
}

function EventRow({ row, imageProvider, site, onOpenSnapshot }: EventRowProps) {
  const url = imageProvider.getEventImageUrl(row, { site });
  const timeLabel = formatEventTimestamp(row.timestamp, site.timezone);
  const tone = activityTone(row.activity_type);
  const isPriority = isPriorityIncident(row);

  return (
    <View flexDirection="row" py="sm" px="sm" borderBottomWidth={1} borderColor="borderCard" alignItems="flex-start">
      <Cell column={COLUMNS[0]!}>
        <Text textStyle="xs/regular" color="textMuted">
          {timeLabel}
        </Text>
      </Cell>
      <Cell column={COLUMNS[1]!}>
        <Text textStyle="xs/semibold" color="text">
          {row.asset_id}
        </Text>
        <Text textStyle="xs/regular" color="textMuted">
          {row.camera}
        </Text>
      </Cell>
      <Cell column={COLUMNS[2]!}>
        <View gap="xxs">
          <View alignSelf="flex-start" bg={tone.bg} px="sm" py="xxs" rounded={999}>
            <Text textStyle="xs/semibold" color={tone.fg}>
              {row.activity_type}
            </Text>
          </View>
          {isPriority ?
            /**
             * Mirrors the hero list's "priority" classification. Same
             * `isPriorityIncident` predicate the KPI sums use, so a
             * row tagged here is one of the rows the hero surfaces.
             */
            <View alignSelf="flex-start" bg="danger" px="sm" py="xxs" rounded={999}>
              <Text textStyle="xs/semibold" color="textReversed" textTransform="uppercase">
                Priority
              </Text>
            </View>
          : null}
        </View>
      </Cell>
      <Cell column={COLUMNS[3]!}>
        <View gap="xxs">
          <Text textStyle="xs/regular" color="text">
            {row.description}
          </Text>
          {row.interesting_tags.length > 0 ?
            <Text textStyle="xs/regular" color="textMuted">
              {row.interesting_tags.join(" · ")}
            </Text>
          : null}
        </View>
      </Cell>
      <Cell column={COLUMNS[4]!}>
        {url ?
          <Pressable
            onPress={() => onOpenSnapshot(row)}
            accessibilityRole="button"
            accessibilityLabel={`Open snapshot from ${row.camera}`}
            // `cursor: "zoom-in"` is a web-only CSS value — RN's typings only
            // expose `pointer`/`auto`. The cast keeps it on web without
            // affecting native (this component is mounted web-only anyway).
            style={({ pressed }) =>
              ({ opacity: pressed ? 0.85 : 1, alignSelf: "flex-start", cursor: "zoom-in" }) as object
            }
          >
            <LazySnapshot uri={url} width={96} height={72} borderRadius={6} alt={`Snapshot from ${row.camera}`} />
          </Pressable>
        : <View width={96} height={72} bg="muted" rounded={6} alignItems="center" justifyContent="center">
            <Text textStyle="xs/regular" color="textMuted">
              —
            </Text>
          </View>
        }
      </Cell>
    </View>
  );
}
